import {GroupedEventList} from '@/components/groupedEventList/groupedEventList';
import {ListSubscriptionPrompt} from '@/components/listSubscriptionPrompt/listSubscriptionPrompt';
import {ListAdminBar} from '@/components/listAdminBar/listAdminBar';
import {ListEmptyState} from '@/components/listEmptyState/listEmptyState';
import {ListHero} from '@/components/listHero/listHero';
import {PaginationGroup} from '@/components/paginationGroup/paginationGroup';
import {Button} from '@/components/ui/button';
import {Footer} from '@/components/ui/footer';
import {Navbar} from '@/components/ui/navbar';
import {toastManager} from '@/components/ui/toast';
import {Typography} from '@/components/ui/typography';
import {buildListJsonLd} from '@/helpers/buildListJsonLd';
import {followEventList} from '@/server/listFollower';
import {getPublicEventList} from '@/server/savedEvent';
import {getProfileName} from '@/utils/getProfileName';
import {Link, createFileRoute, useRouter} from '@tanstack/react-router';
import {useEffect, useRef} from 'react';
import {z} from 'zod';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://wayra.cc';

const DEFAULT_SEARCH = {page: 1, pageSize: 12} as const;

const riderListSearchSchema = z.object({
  page: z.number().int().positive().optional().catch(undefined),
  pageSize: z.number().int().positive().max(50).optional().catch(undefined),
  follow: z.coerce.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/rider/$profileId/list/$listId')({
  validateSearch: (search) => riderListSearchSchema.parse(search),
  loaderDeps: ({search}) => ({
    page: search.page ?? DEFAULT_SEARCH.page,
    pageSize: search.pageSize ?? DEFAULT_SEARCH.pageSize,
  }),
  loader: ({params, deps}) =>
    getPublicEventList({
      data: {
        profileId: params.profileId,
        listId: params.listId,
        page: deps.page,
        pageSize: deps.pageSize,
      },
    }),
  head: ({params, loaderData}) => {
    if (!loaderData) return {meta: [{title: 'List not found · Wayra'}]};
    const {list, creator, events, pagination} = loaderData;
    const creatorName = getProfileName(creator);
    const description =
      list.description ??
      `${pagination.totalCount} cycling events curated by ${creatorName}`;
    const ogImageUrl = `${SITE_URL}/api/og/list/${list.id}`;
    const canonicalUrl = `${SITE_URL}/rider/${params.profileId}/list/${list.id}`;
    const jsonLd = JSON.stringify(
      buildListJsonLd({list, events, pagination})
    ).replace(/<\//g, '<\\/');

    return {
      meta: [
        {title: `${list.name} · ${creatorName} · Wayra`},
        {name: 'description', content: description},
        {property: 'og:title', content: list.name},
        {property: 'og:description', content: description},
        {property: 'og:image', content: ogImageUrl},
        {property: 'og:image:width', content: '1200'},
        {property: 'og:image:height', content: '630'},
        {property: 'og:image:alt', content: list.name},
        {property: 'og:type', content: 'website'},
        {property: 'og:url', content: canonicalUrl},
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'twitter:title', content: list.name},
        {name: 'twitter:description', content: description},
        {name: 'twitter:image', content: ogImageUrl},
        {name: 'twitter:image:alt', content: list.name},
      ],
      links: [
        {rel: 'canonical', href: canonicalUrl},
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd,
        },
      ],
    };
  },
  errorComponent: PublicListNotFound,
  pendingComponent: PublicListPending,
  component: PublicListPage,
});

function PublicListPage() {
  const {
    list,
    creator,
    events,
    pagination,
    isOwner,
    followerCount,
    isFollowing,
  } = Route.useLoaderData();
  const {follow} = Route.useSearch();
  const router = useRouter();
  const creatorName = getProfileName(creator);

  const autoFollowHandled = useRef(false);

  useEffect(() => {
    if (!follow || autoFollowHandled.current) return;
    autoFollowHandled.current = true;

    const url = new URL(window.location.href);
    url.searchParams.delete('follow');
    history.replaceState(null, '', url.pathname + url.search);

    if (isOwner || isFollowing) return;

    followEventList({data: list.id})
      .then(() => {
        toastManager.add({title: 'Following list', type: 'success'});
        router.invalidate();
      })
      .catch(() => {
        toastManager.add({title: 'Failed to follow list', type: 'error'});
      });
  }, [follow, isOwner, isFollowing, list.id, router]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex flex-col flex-1">
        {isOwner ? <ListAdminBar listId={list.id} /> : null}
        <ListHero
          list={list}
          creator={creator}
          totalCount={pagination.totalCount}
          followerCount={followerCount}
          isOwner={isOwner}
          isFollowing={isFollowing}
        />
        <ListSubscriptionPrompt
          listId={list.id}
          isOwner={isOwner}
          isFollowing={isFollowing}
          creatorName={creatorName}
          eventCount={events.length}
        />

        {events.length === 0 ? (
          <ListEmptyState creatorName={creatorName} />
        ) : (
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pb-12">
            <GroupedEventList events={events} />

            {pagination.totalPages > 1 ? (
              <PaginationGroup pagination={pagination} />
            ) : null}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function PublicListPending() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex flex-col flex-1 gap-6 p-6" aria-busy="true">
        <div className="h-72 w-full animate-pulse rounded-lg bg-neutral-200" />
        <div className="h-10 w-2/3 animate-pulse rounded bg-neutral-200" />
        <div className="h-5 w-1/3 animate-pulse rounded bg-neutral-200" />
      </div>
      <Footer />
    </div>
  );
}

function PublicListNotFound() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
        <Typography level="H3">List not found</Typography>
        <Typography level="B1" className="text-neutral-500">
          This list doesn&apos;t exist or isn&apos;t shared publicly.
        </Typography>
        <Link to="/">
          <Button variant="outline">Back to Wayra</Button>
        </Link>
      </div>
      <Footer />
    </div>
  );
}
